import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "./services/api";
import { socket } from "./socket"; // ✅ ADD THIS

// Reviewer components
import ReviewerDashboard from "./components/ReviewerDashboard";
import ReviewerPendingReviews from "./pages/ReviewerPendingReviews";
import ReviewerReviewedSubmissions from "./pages/ReviewerReviewedSubmissions";
import ReviewerNotifications from "./pages/ReviewerNotifications";
import ReviewerDownloadTemplates from "./pages/ReviewerDownloadTemplates";
import ReviewerSettings from "./pages/ReviewerSettings";

// Admin components
import Dashboard from "./components/Dashboard";
import AdminPendingReviews from "./pages/AdminPendingReviews";
import AdminOverdueESMPs from "./pages/AdminOverdueESMPs";
import AdminApprovedESMPs from "./pages/AdminApprovedESMPs";
import AdminReturnedESMPs from "./pages/AdminReturnedESMPs";
import AdminRejectedESMPs from "./pages/AdminRejectedESMPs";
import ManageRoles from "./pages/ManageRoles";
import Statistics from "./pages/Statistics";
import AuditLogs from "./pages/AuditLogs";
import AdminNotifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import SubmittedESMP from "./pages/SubmittedESMP";

// District components
import DistrictDashboard from "./components/DistrictDashboard";
import DistrictSubmittedESMP from "./pages/district/SubmittedESMP";
import PendingReviews from "./pages/district/PendingReviews";
import OverdueESMP from "./pages/district/OverdueESMP";
import ApprovedESMP from "./pages/district/ApprovedESMP";
import ReturnedESMP from "./pages/district/ReturnedESMP";
import DistrictNotifications from "./pages/district/Notifications";
import SettingsPage from "./pages/district/SettingsPage";
import ViewerDashboard from "./pages/ViewerDashboard";
import ViewerSettings from "./pages/ViewerSettings";
import ViewerStatistics from "./pages/ViewerStatistics";
import ViewerReviewerWorkload from "./pages/ViewerReviewerWorkload";
import ViewerSectorStatistics from "./pages/ViewerSectorStatistics";
import ViewerDownloadAllESMPs from "./pages/ViewerDownloadAllESMPs";

import AccessDenied from "./pages/AccessDenied";
import { DISTRICT_EDO_ROLES } from "./constants/roles";

// Login
import LogIn from "./pages/Login.jsx";

export default function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // 🔐 AUTH HYDRATION (UNCHANGED)
  useEffect(() => {
    const hydrateAuth = async () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (!token) {
        setAuthReady(true);
        return;
      }

      try {
        const res = await api.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const currentUser = res.data?.user || null;
        if (!currentUser) throw new Error("No user payload returned");

        setUser(currentUser);
        localStorage.setItem("user", JSON.stringify(currentUser));
      } catch (err) {
        console.error("Auth hydration failed:", err);

        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem("user");
          }
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      } finally {
        setAuthReady(true);
      }
    };

    hydrateAuth();
  }, []);

  // 🔌 SOCKET CONNECTION (SAFE ADDITION)
  useEffect(() => {
    if (!user) {
      if (socket.connected) socket.disconnect();
      return;
    }

    socket.auth = {
      userId: user.id,
      role: user.role,
    };

    socket.connect();
    console.log("🔌 Socket connected for user:", user.id);

    return () => {
      socket.disconnect();
      console.log("🔌 Socket disconnected");
    };
  }, [user]);

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!authReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <p className="text-gray-600">Loading...</p>
        </div>
      );
    }

    if (!user) return <Navigate to="/" replace />;
    if (!allowedRoles.includes(user.role)) return <AccessDenied />;
    return children;
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LogIn setUser={setUser} />} />
        <Route path="/403" element={<AccessDenied />} />

        {/* REVIEWER ROUTES */}
        <Route
          path="/reviewer-dashboard"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer-pending-reviews"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerPendingReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer-reviewed-submissions"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerReviewedSubmissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer-notifications"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer-download-templates"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerDownloadTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviewer/settings"
          element={
            <ProtectedRoute allowedRoles={["reviewer"]}>
              <ReviewerSettings />
            </ProtectedRoute>
          }
        />

        {/* ADMIN ROUTES */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/submitted-esmp"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <SubmittedESMP />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/pending"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminPendingReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/overdue"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminOverdueESMPs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approved"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminApprovedESMPs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/returned"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminReturnedESMPs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/rejected"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminRejectedESMPs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-roles"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <ManageRoles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* DISTRICT ROUTES */}
        <Route
          path="/district-dashboard"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <DistrictDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/submitted-esmp"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <DistrictSubmittedESMP />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/pending-reviews"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <PendingReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/overdue"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <OverdueESMP />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/approved-esmp"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <ApprovedESMP />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/returned-esmp"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <ReturnedESMP />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/notifications"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <DistrictNotifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/district/settings"
          element={
            <ProtectedRoute allowedRoles={DISTRICT_EDO_ROLES}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* VIEWER ROUTE (READ-ONLY) */}
        <Route
          path="/viewer-dashboard"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/download-all"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerDownloadAllESMPs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/settings"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/statistics"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerStatistics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/reviewer-workload"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerReviewerWorkload />
            </ProtectedRoute>
          }
        />
        <Route
          path="/viewer/statistics/sectors"
          element={
            <ProtectedRoute allowedRoles={["viewer"]}>
              <ViewerSectorStatistics />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
